import Thunk from 'src/thunks/index';
import discussionActions from 'src/store/model/discussion';
import discussionReplyActions from 'src/store/model/discussionReply';
import { Data, List } from 'src/interfaces/general';
import { fetchAndCheckClassUser, fetchClassById } from 'src/thunks/classes';
import { fetchTopicContentById } from 'src/thunks/topic_content';
import { unwrapResult } from '@reduxjs/toolkit';

const discussion = new Thunk('discussions');

export type DiscussionPayload = Partial<Model.Discussion>;

export const fetchDiscussions = discussion.fetch<
  List<Model.Discussion>,
  SearchParams.Discussion
>({
  onSuccess({ dispatch, data, args }) {
    dispatch(discussionActions.updateEntities(data.data));
    dispatch(
      discussionReplyActions.updateEntities(
        data.data
          ?.flatMap((d) => d.replies)
          .filter(Boolean) as Model.Discussion[],
      ),
    );
    dispatch(
      discussionActions.updatePagination({
        ...data.paginator,
      }),
    );
  },
});

export const fetchDiscussionDetail = discussion.fetchOne<
  Data<Model.Discussion>
>({
  onSuccess({ dispatch, data }) {
    dispatch(discussionActions.updateEntity(data.data));
    dispatch(discussionReplyActions.updateEntities(data.data.replies || []));
  },
});

export const createReply = discussion.create<
  Data<Model.Discussion>,
  DiscussionPayload
>({
  onSuccess({ dispatch, data, args }) {
    dispatch(
      discussionReplyActions.updateEntity({ ...data.data, user: args.user }),
    );
  },
  shouldNotify: false,
  onError({ error, dispatch, args }) {
    dispatch(discussionReplyActions.updateError(args));
  },
});

export const updateReply = discussion.update<
  Data<Model.Discussion>,
  DiscussionPayload
>({
  onSuccess({ dispatch, data, args }) {
    dispatch(
      discussionReplyActions.updateEntity({
        ...data.data,
        user: args.user,
        error: false,
      }),
    );
  },
  shouldNotify: false,
  onError({ error, dispatch, args }) {
    dispatch(discussionReplyActions.updateError(args));
  },
});

export const deleteReply = discussion.remove<
  Data<Model.Discussion>,
  DiscussionPayload
>({
  onSuccess({ dispatch, data, args }) {
    dispatch(discussionReplyActions.removeEntity(args));
  },
});

export const createDiscussion = discussion.create<
  Data<Model.Discussion>,
  DiscussionPayload
>({
  onSuccess({ dispatch, data, args }) {
    dispatch(
      discussionActions.createEntity({
        ...data.data,
        user: args.user,
        user_id: args.user?.id,
      }),
    );
  },
  shouldNotify: false,
  onError({ error, dispatch, args }) {
    dispatch(discussionActions.updateError(args));
  },
});

export const updateDiscussion = discussion.update<
  Data<Model.Discussion>,
  DiscussionPayload
>({
  onSuccess({ dispatch, data, args }) {
    dispatch(
      discussionActions.updateEntity({
        ...data.data,
        user: args.user,
        user_id: args.user_id,
        error: false,
      }),
    );
  },
  shouldNotify: false,
  onError({ error, dispatch, args }) {
    dispatch(discussionActions.updateError(args));
  },
});

export const deleteDiscussion = discussion.remove<
  Data<Model.Discussion>,
  DiscussionPayload
>({
  onSuccess({ dispatch, args }) {
    dispatch(discussionActions.removeEntity(args));
  },
});

export const fetchResources = discussion.fetchResources<
  void,
  SearchParams.Note
>('discussions', async (arg, { dispatch, getState }) => {
  await fetchAndCheckClassUser(arg.class_id!, dispatch, getState());

  const [classResult] = await Promise.all([
    dispatch(fetchClassById({ id: arg.class_id! })).then(unwrapResult),
    dispatch(fetchTopicContentById({ id: arg.class_topic_content_id! })),
  ]);

  if (classResult.data.type !== 'CISL') {
    await dispatch(
      fetchDiscussions({
        ...arg,
        order_by: 'created_at,desc',
        page:
          getState().model.discussion.pagination.next_page === null
            ? String(getState().model.discussion.pagination.total_pages)
            : String(getState().model.discussion.pagination.next_page),
      }),
    );
  }
});
